﻿import React, { useContext, useMemo, useRef, useEffect, useState } from 'react';
import Context, { context_type } from '@Util/Context';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { insertCharacterIntoString, generate_unique_id } from '@Util/Utils';
import Startpage_Total_Time_Header from '@Elements/Startpage_Total_Time_Header';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import svLocale from '@fullcalendar/core/locales/sv';
import interactionPlugin from "@fullcalendar/interaction";
import { Tooltip } from 'react-tooltip';
import Content_Container from '@Elements/Content_Container';
import Context_Menu from '@Modules/Context_Menu';
import Edit_Schedule_Entry_Popup from '@Modules/Popups/Edit_Schedule_Entry_Popup';

import {
    Mouse_Position_Type,
    Weeks_Timelog_Filter
} from '@Util/Types';

export default function Calendar(): JSX.Element {
    const [t] = useTranslation();
    const calendar_fallback_startime: string = "07:00";
    const cal = useRef() as any;

    const {
        weeks_timelog_filter,
        set_weeks_timelog_filter,
        todays_time_log,
        weeks_time_log,
        fetch_this_weeks_timelog,
        delete_time_object,
        split_time_object,
        align_time_object
    }: context_type = useContext(Context);

    const [selected_calendar_item, set_selected_calendar_item] = useState<string>("");
    const [mouse_position, set_mouse_position] = useState<Mouse_Position_Type>({ top: 0, left: 0 });
    const [edit_mode, set_edit_mode] = useState<boolean>(false);

    // GENERATES CALENDAR DATA
    const calendar_data = useMemo(() => {
        let show_weekends: boolean = false;
        let temp = [];
        let temp_min_time = null;
    
        for (let i = 0; i < weeks_time_log.length; i++) {
            let week_day_start = moment(weeks_time_log[i].startDate).day();
            let week_day_end = moment(weeks_time_log[i].endDate).day();
    
            if ((week_day_start == 6) || (week_day_start == 0) || (week_day_end == 6) || (week_day_end == 0)) {
                show_weekends = true;
            }
    
            if (temp_min_time === null) {
                temp_min_time = moment(weeks_time_log[i].startDate).startOf("hour").format("HHmm");
            }
    
            if (parseInt(temp_min_time) > parseInt(moment(weeks_time_log[i].startDate).format("HHmm"))) {
                temp_min_time = moment(weeks_time_log[i].startDate).startOf("hour").format("HHmm");
            }
    
            temp.push({
                title: weeks_time_log[i].projectName + ' #' + weeks_time_log[i].id,
                start: weeks_time_log[i].startDate,
                end: weeks_time_log[i].endDate,
                backgroundColor: 'rgb(' + weeks_time_log[i].color + ')',
                textColor: "#ffffff",
                overlap: false,
                extendedProps: {
                    customerName: weeks_time_log[i].customerName
                }
            });
        }
    
        return {
            data: temp,
            calendar_data_initialized: true,
            min_time: temp_min_time != null ? insertCharacterIntoString(temp_min_time, ":", 2) : calendar_fallback_startime,
            show_weekends
        };
    }, [weeks_time_log]);

    // UPDATES THE CALENDAR WHEN THE DATA IS LOADED
    useEffect(() => {
        if (cal) {
            cal?.current?.getApi().updateSize();
        }
    }, [calendar_data])

    // LOADS INITIAL DATA & FETCHES DATA AGAIN WHEN WEEK FILTER IS CHANGING
    useEffect(() => {
        fetch_this_weeks_timelog();
    }, [weeks_timelog_filter]);

    // DISPLAY PREVIOUS WEEK
    async function handle_display_previous_week(): Promise<void> {
        const o: Weeks_Timelog_Filter = {
            beginning_of_this_week: moment(weeks_timelog_filter.beginning_of_this_week).subtract(1, 'weeks').startOf('week'),
            end_of_this_week: moment(weeks_timelog_filter.end_of_this_week).subtract(1, 'weeks').endOf('week'),
            week_number: moment(weeks_timelog_filter.beginning_of_this_week).subtract(1, 'weeks').startOf('week'),
        };

        set_weeks_timelog_filter(o);
        cal?.current?.getApi().prev();
    }

    // DISPLAY NEXT WEEK
    async function handle_display_next_week(): Promise<void> {
        const o: Weeks_Timelog_Filter = {
            beginning_of_this_week: moment(weeks_timelog_filter.beginning_of_this_week).add(1, 'weeks').startOf('week'),
            end_of_this_week: moment(weeks_timelog_filter.end_of_this_week).add(1, 'weeks').endOf('week'),
            week_number: moment(weeks_timelog_filter.beginning_of_this_week).add(1, 'weeks').startOf('week'),
        };

        set_weeks_timelog_filter(o);
        cal?.current?.getApi().next();
    }

    // DISPLAY THIS WEEK
    async function handle_display_this_week(): Promise<void> {
        const o: Weeks_Timelog_Filter = {
            beginning_of_this_week: moment().startOf('week'),
            end_of_this_week: moment().endOf('week'),
            week_number: moment(),
        };

        set_weeks_timelog_filter(o);
        cal?.current?.getApi().today();
    }

    return (
        <section className="Module">
            <Startpage_Total_Time_Header
                todays_time_log={todays_time_log}
                weeks_time_log={weeks_time_log}
            />
            <Content_Container>
                <FullCalendar
                    ref={cal}
                    plugins={[timeGridPlugin, interactionPlugin]}
                    initialView="timeGridWeek"
                    weekends={calendar_data?.show_weekends}
                    locale={svLocale}
                    direction="ltr"
                    height="77vh"
                    firstDay={1}
                    slotDuration="00:20:00"
                    slotLabelInterval="01:00:00"
                    slotMinTime={calendar_data?.min_time}
                    scrollTime="07:00"
                    allDaySlot={false}
                    defaultTimedEventDuration="00:01"
                    eventMinHeight={3}
                    headerToolbar={{
                        start: '',
                        center: '',
                        end: ''
                    }}
                    footerToolbar={{
                        start: '',
                        center: '',
                        end: 'todayButton prevButton,nextButton'
                    }}
                    slotLabelFormat={{
                        hour: "2-digit",
                        minute: "2-digit",
                        omitZeroMinute: false,
                        meridiem: "short"
                    }}
                    eventTimeFormat={{
                        hour: "2-digit",
                        minute: "2-digit",
                        meridiem: false,
                        hour12: false
                    }}
                    nowIndicator={true}
                    events={calendar_data?.data}
                    eventOverlap={false}
                    slotEventOverlap={false}
                    eventClick={
                        function (arg) {
                            set_selected_calendar_item(arg.event.title.substring(arg.event.title.length, arg.event.title.indexOf('#')).substring(1));
                            set_mouse_position({ top: arg.jsEvent.clientY, left: arg.jsEvent.clientX });
                        }
                    }
                    eventContent={
                        function (arg) {
                            const tooltip_id: string = generate_unique_id();

                            return (
                                <>
                                    <div
                                        className='fc-event-main-frame'
                                        data-tooltip-id={`calendar_item_${tooltip_id}`}
                                        data-tooltip-place="top"
                                        data-tooltip-delay-show={500}
                                    >
                                        {
                                            arg.timeText &&
                                            <div className='fc-event-time'>{arg.timeText}</div>
                                        }
                                        <div className='fc-event-title-container'>
                                            <div className='fc-event-title fc-sticky'>
                                                {arg.event.title || <>&nbsp;</>}
                                            </div>
                                        </div>
                                    </div>
                                    <Tooltip id={`calendar_item_${tooltip_id}`} opacity="1">
                                        <>
                                            {
                                                arg?.event?.extendedProps?.customerName ?
                                                    <span className="Tooltip_Row">Kund: <span>{arg?.event?.extendedProps?.customerName}</span></span>
                                                    : null
                                            }
                                            {
                                                arg?.event?.title ?
                                                    <span className="Tooltip_Row">Projekt: <span>{arg?.event?.title}</span></span> :
                                                    null
                                            }
                                            {
                                                arg?.event?.startStr ?
                                                    <span className="Tooltip_Row">Start: <span>{moment(arg?.event?.startStr).format("HH:mm")}</span></span> :
                                                    null
                                            }
                                            {
                                                arg?.event?.endStr ?
                                                    <span className="Tooltip_Row">Slut: <span>{moment(arg?.event?.endStr).format("HH:mm")}</span></span> :
                                                    null
                                            }
                                        </>
                                    </Tooltip>
                                </>
                            );
                        }
                    }
                    customButtons={{
                        prevButton: {
                            hint: t('Button.Previous'),
                            icon: "chevron-left",
                            click: handle_display_previous_week
                        },
                        nextButton: {
                            hint: t('Button.Next'),
                            icon: "chevron-right",
                            click: handle_display_next_week
                        },
                        todayButton: {
                            text: t('Button.Today'),
                            click: handle_display_this_week
                        }
                    }}
                />
                <Context_Menu
                    entry_id={selected_calendar_item}
                    edit_mode={edit_mode}
                    set_edit_mode={set_edit_mode}
                    position={mouse_position}
                    delete_time_object={delete_time_object}
                    split_time_object={split_time_object}
                    align_time_object={align_time_object}
                    selected_chart_item={selected_calendar_item}
                    set_selected_chart_item={set_selected_calendar_item}
                />
                <Edit_Schedule_Entry_Popup
                    entry_id={selected_calendar_item}
                    edit_mode={edit_mode}
                    set_edit_mode={set_edit_mode}
                />
            </Content_Container>
        </section >
    )
}